import type {
  AddressPoint,
  AddressSuggestion,
} from "../../shared/address-search";

export const ADDRESS_SELECTION_REQUIRED_MESSAGE =
  "Pilih patokan untuk ongkir dari hasil pencarian terlebih dahulu.";
export const LOCATION_CONFIRMATION_REQUIRED_MESSAGE =
  "Pilih patokan untuk ongkir dari hasil pencarian terlebih dahulu.";

export interface CheckoutDeliveryLocation {
  branchId: string;
  suggestion: AddressSuggestion;
  draftPoint: AddressPoint;
  confirmed?: {
    addressText: string;
    point: AddressPoint;
  };
}

export function isLocalCheckoutHostname(hostname: string): boolean {
  return ["localhost", "127.0.0.1", "::1"].includes(hostname.toLowerCase());
}

export function selectedDestination(
  suggestion: AddressSuggestion | undefined,
): { lat: number; lng: number } {
  return validDestination(suggestion?.point, ADDRESS_SELECTION_REQUIRED_MESSAGE);
}

export function validDestination(
  point: AddressPoint | undefined | null,
  message = LOCATION_CONFIRMATION_REQUIRED_MESSAGE,
): AddressPoint {
  const lat = point?.lat;
  const lng = point?.lng;
  if (
    lat === undefined ||
    lng === undefined ||
    !Number.isFinite(lat) ||
    !Number.isFinite(lng) ||
    lat < -90 ||
    lat > 90 ||
    lng < -180 ||
    lng > 180
  ) {
    throw new Error(message);
  }
  return { lat, lng };
}

export function candidateDeliveryLocation(
  branchId: string,
  suggestion: AddressSuggestion,
): CheckoutDeliveryLocation {
  const point = selectedDestination(suggestion);
  return {
    branchId,
    suggestion: {
      ...suggestion,
      point: { ...point },
    },
    draftPoint: { ...point },
  };
}

export function moveDeliveryPin(
  current: CheckoutDeliveryLocation,
  point: AddressPoint,
): CheckoutDeliveryLocation {
  return {
    ...current,
    draftPoint: validDestination(point),
    confirmed: undefined,
  };
}

export function confirmDeliveryPin(
  current: CheckoutDeliveryLocation,
  addressText: string,
): CheckoutDeliveryLocation {
  return {
    ...current,
    confirmed: {
      addressText: addressText.trim(),
      point: validDestination(current.draftPoint),
    },
  };
}

export function activeConfirmedDestination(
  current: CheckoutDeliveryLocation | undefined,
  branchId: string,
  addressText: string,
): AddressPoint | undefined {
  if (
    current?.branchId !== branchId ||
    current.confirmed?.addressText !== addressText.trim()
  ) return undefined;
  return current.confirmed.point;
}

export function resolveCheckoutDestination(input: {
  addressSearchRequired: boolean;
  confirmedPoint?: AddressPoint;
}): Promise<{ lat: number; lng: number }> {
  if (input.addressSearchRequired) {
    return Promise.resolve().then(() => validDestination(input.confirmedPoint));
  }
  return Promise.reject(new Error(LOCATION_CONFIRMATION_REQUIRED_MESSAGE));
}
